import React, { useState, useEffect } from 'react';
import { Send, MessageSquare, AlertCircle, RefreshCw, ChevronDown, ChevronUp, LogOut, Play, Pause, Image, FileText, Video, Volume2, X } from 'lucide-react';
import ResizableLayout from './components/ResizableLayout';

interface User {
  id: string;
  email: string;
  name: string;
}

interface Message {
  id: string;
  content: string;
  timestamp: string;
  from_number: string;
  to_number: string;
  type: string;
  sender_name: string;
  status: string;
  is_read: number;
  media_url?: string;
}

interface Template {
  name: string;
  language: string;
  status: string;
  category: string;
  components: {
    type: string;
    text?: string;
    parameters?: Array<{
      type: string;
      text: string;
    }>;
  }[];
}

interface CountryCodeSummary {
  countryCode: string;
  unreadCount: number;
  totalConversations: number;
}

// Country code to country info mapping with 2-letter codes
const COUNTRY_CODES: { [key: string]: { name: string; code2: string } } = {
  '+1': { name: 'US/CA', code2: 'US' },
  '+20': { name: 'Egypt', code2: 'EG' },
  '+30': { name: 'Greece', code2: 'GR' },
  '+34': { name: 'Spain', code2: 'ES' },
  '+32': { name: 'Belgium', code2: 'BE' },
  '+33': { name: 'France', code2: 'FR' },
  '+46': { name: 'Poland', code2: 'SE' },
  '+48': { name: 'Poland', code2: 'PL' },
  '+49': { name: 'Germany', code2: 'DE' },
  '+39': { name: 'Italy', code2: 'IT' },
  '+44': { name: 'UK', code2: 'GB' },
  '+351': { name: 'Portugal', code2: 'PT' },
  '+52': { name: 'Mexico', code2: 'MX' },
  '+54': { name: 'Argentina', code2: 'AR' },
  '+55': { name: 'Brazil', code2: 'BR' },
  '+56': { name: 'Chile', code2: 'CL' },
  '+57': { name: 'Colombia', code2: 'CO' },
  '+58': { name: 'Venezuela', code2: 'VE' },
  '+591': { name: 'Bolivia', code2: 'BO' },
  '+593': { name: 'Ecuador', code2: 'EC' },
  '+594': { name: 'French Guiana', code2: 'GF' },
  '+595': { name: 'Paraguay', code2: 'PY' },
  '+596': { name: 'Martinique', code2: 'MQ' },
  '+597': { name: 'Suriname', code2: 'SR' },
  '+598': { name: 'Uruguay', code2: 'UY' },
  '+62': { name: 'Indonesia', code2: 'ID' },
  '+63': { name: 'Philippines', code2: 'PH' },
  '+65': { name: 'Singapore', code2: 'SG' },
  '+66': { name: 'Thailand', code2: 'TH' },
  '+51': { name: 'Peru', code2: 'PE' },
  '+7': { name: 'Russia/Kazakhstan', code2: 'RU' },
  '+81': { name: 'Japan', code2: 'JP' },
  '+82': { name: 'South Korea', code2: 'KR' },
  '+86': { name: 'China', code2: 'CN' },
  '+90': { name: 'Turkey', code2: 'TR' },
  '+91': { name: 'India', code2: 'IN' },
  '+92': { name: 'Pakistan', code2: 'PK' },
  '+93': { name: 'Afghanistan', code2: 'AF' },
  '+94': { name: 'Sri Lanka', code2: 'LK' },
  '+95': { name: 'Myanmar', code2: 'MM' },
  '+98': { name: 'Iran', code2: 'IR' },
  '+212': { name: 'Morocco', code2: 'MA' },
  '+213': { name: 'Algeria', code2: 'DZ' },
  '+216': { name: 'Tunisia', code2: 'TN' },
  '+218': { name: 'Libya', code2: 'LY' },
  '+220': { name: 'Gambia', code2: 'GM' },
  '+221': { name: 'Senegal', code2: 'SN' },
  '+222': { name: 'Mauritania', code2: 'MR' },
  '+223': { name: 'Mali', code2: 'ML' },
  '+224': { name: 'Guinea', code2: 'GN' },
  '+225': { name: 'Ivory Coast', code2: 'CI' },
  '+226': { name: 'Burkina Faso', code2: 'BF' },
  '+227': { name: 'Niger', code2: 'NE' },
  '+228': { name: 'Togo', code2: 'TG' },
  '+229': { name: 'Benin', code2: 'BJ' },
  '+230': { name: 'Mauritius', code2: 'MU' },
  '+231': { name: 'Liberia', code2: 'LR' },
  '+232': { name: 'Sierra Leone', code2: 'SL' },
  '+233': { name: 'Ghana', code2: 'GH' },
  '+234': { name: 'Nigeria', code2: 'NG' },
  '+235': { name: 'Chad', code2: 'TD' },
  '+236': { name: 'Central African Republic', code2: 'CF' },
  '+237': { name: 'Cameroon', code2: 'CM' },
  '+238': { name: 'Cape Verde', code2: 'CV' },
  '+239': { name: 'São Tomé and Príncipe', code2: 'ST' },
  '+240': { name: 'Equatorial Guinea', code2: 'GQ' },
  '+241': { name: 'Gabon', code2: 'GA' },
  '+242': { name: 'Republic of the Congo', code2: 'CG' },
  '+243': { name: 'Democratic Republic of the Congo', code2: 'CD' },
  '+244': { name: 'Angola', code2: 'AO' },
  '+245': { name: 'Guinea-Bissau', code2: 'GW' },
  '+246': { name: 'British Indian Ocean Territory', code2: 'IO' },
  '+248': { name: 'Seychelles', code2: 'SC' },
  '+249': { name: 'Sudan', code2: 'SD' },
  '+250': { name: 'Rwanda', code2: 'RW' },
  '+251': { name: 'Ethiopia', code2: 'ET' },
  '+252': { name: 'Somalia', code2: 'SO' },
  '+253': { name: 'Djibouti', code2: 'DJ' },
  '+254': { name: 'Kenya', code2: 'KE' },
  '+255': { name: 'Tanzania', code2: 'TZ' },
  '+256': { name: 'Uganda', code2: 'UG' },
  '+257': { name: 'Burundi', code2: 'BI' },
  '+258': { name: 'Mozambique', code2: 'MZ' },
  '+260': { name: 'Zambia', code2: 'ZM' },
  '+261': { name: 'Madagascar', code2: 'MG' },
  '+262': { name: 'Réunion/Mayotte', code2: 'RE' },
  '+263': { name: 'Zimbabwe', code2: 'ZW' },
  '+264': { name: 'Namibia', code2: 'NA' },
  '+265': { name: 'Malawi', code2: 'MW' },
  '+266': { name: 'Lesotho', code2: 'LS' },
  '+267': { name: 'Botswana', code2: 'BW' },
  '+268': { name: 'Eswatini', code2: 'SZ' },
  '+269': { name: 'Comoros', code2: 'KM' },
  '+290': { name: 'Saint Helena', code2: 'SH' },
  '+291': { name: 'Eritrea', code2: 'ER' },
  '+297': { name: 'Aruba', code2: 'AW' },
  '+298': { name: 'Faroe Islands', code2: 'FO' },
  '+299': { name: 'Greenland', code2: 'GL' },
  '+350': { name: 'Gibraltar', code2: 'GI' },
  '+352': { name: 'Luxembourg', code2: 'LU' },
  '+353': { name: 'Ireland', code2: 'IE' },
  '+354': { name: 'Iceland', code2: 'IS' },
  '+355': { name: 'Albania', code2: 'AL' },
  '+356': { name: 'Malta', code2: 'MT' },
  '+357': { name: 'Cyprus', code2: 'CY' },
  '+358': { name: 'Finland', code2: 'FI' },
  '+359': { name: 'Bulgaria', code2: 'BG' },
  '+370': { name: 'Lithuania', code2: 'LT' },
  '+371': { name: 'Latvia', code2: 'LV' },
  '+372': { name: 'Estonia', code2: 'EE' },
  '+373': { name: 'Moldova', code2: 'MD' },
  '+374': { name: 'Armenia', code2: 'AM' },
  '+375': { name: 'Belarus', code2: 'BY' },
  '+376': { name: 'Andorra', code2: 'AD' },
  '+377': { name: 'Monaco', code2: 'MC' },
  '+378': { name: 'San Marino', code2: 'SM' },
  '+380': { name: 'Ukraine', code2: 'UA' },
  '+381': { name: 'Serbia', code2: 'RS' },
  '+382': { name: 'Montenegro', code2: 'ME' },
  '+383': { name: 'Kosovo', code2: 'XK' },
  '+385': { name: 'Croatia', code2: 'HR' },
  '+386': { name: 'Slovenia', code2: 'SI' },
  '+387': { name: 'Bosnia and Herzegovina', code2: 'BA' },
  '+389': { name: 'North Macedonia', code2: 'MK' },
  '+420': { name: 'Czech Republic', code2: 'CZ' },
  '+421': { name: 'Slovakia', code2: 'SK' },
  '+423': { name: 'Liechtenstein', code2: 'LI' },
  '+500': { name: 'Falkland Islands', code2: 'FK' },
  '+501': { name: 'Belize', code2: 'BZ' },
  '+502': { name: 'Guatemala', code2: 'GT' },
  '+503': { name: 'El Salvador', code2: 'SV' },
  '+504': { name: 'Honduras', code2: 'HN' },
  '+505': { name: 'Nicaragua', code2: 'NI' },
  '+506': { name: 'Costa Rica', code2: 'CR' },
  '+507': { name: 'Panama', code2: 'PA' },
  '+508': { name: 'Saint Pierre and Miquelon', code2: 'PM' },
  '+509': { name: 'Haiti', code2: 'HT' },
  '+590': { name: 'Guadeloupe', code2: 'GP' },
  '+592': { name: 'Guyana', code2: 'GY' },
  '+599': { name: 'Curaçao', code2: 'CW' },
  '+670': { name: 'East Timor', code2: 'TL' },
  '+672': { name: 'Australian Antarctic Territory', code2: 'AQ' },
  '+673': { name: 'Brunei', code2: 'BN' },
  '+674': { name: 'Nauru', code2: 'NR' },
  '+675': { name: 'Papua New Guinea', code2: 'PG' },
  '+676': { name: 'Tonga', code2: 'TO' },
  '+677': { name: 'Solomon Islands', code2: 'SB' },
  '+678': { name: 'Vanuatu', code2: 'VU' },
  '+679': { name: 'Fiji', code2: 'FJ' },
  '+680': { name: 'Palau', code2: 'PW' },
  '+681': { name: 'Wallis and Futuna', code2: 'WF' },
  '+682': { name: 'Cook Islands', code2: 'CK' },
  '+683': { name: 'Niue', code2: 'NU' },
  '+684': { name: 'American Samoa', code2: 'AS' },
  '+685': { name: 'Samoa', code2: 'WS' },
  '+686': { name: 'Kiribati', code2: 'KI' },
  '+687': { name: 'New Caledonia', code2: 'NC' },
  '+688': { name: 'Tuvalu', code2: 'TV' },
  '+689': { name: 'French Polynesia', code2: 'PF' },
  '+690': { name: 'Tokelau', code2: 'TK' },
  '+691': { name: 'Micronesia', code2: 'FM' },
  '+692': { name: 'Marshall Islands', code2: 'MH' },
  '+850': { name: 'North Korea', code2: 'KP' },
  '+852': { name: 'Hong Kong', code2: 'HK' },
  '+853': { name: 'Macau', code2: 'MO' },
  '+855': { name: 'Cambodia', code2: 'KH' },
  '+856': { name: 'Laos', code2: 'LA' },
  '+880': { name: 'Bangladesh', code2: 'BD' },
  '+886': { name: 'Taiwan', code2: 'TW' },
  '+960': { name: 'Maldives', code2: 'MV' },
  '+961': { name: 'Lebanon', code2: 'LB' },
  '+962': { name: 'Jordan', code2: 'JO' },
  '+963': { name: 'Syria', code2: 'SY' },
  '+964': { name: 'Iraq', code2: 'IQ' },
  '+965': { name: 'Kuwait', code2: 'KW' },
  '+966': { name: 'Saudi Arabia', code2: 'SA' },
  '+967': { name: 'Yemen', code2: 'YE' },
  '+968': { name: 'Oman', code2: 'OM' },
  '+970': { name: 'Palestine', code2: 'PS' },
  '+971': { name: 'United Arab Emirates', code2: 'AE' },
  '+972': { name: 'Israel', code2: 'IL' },
  '+973': { name: 'Bahrain', code2: 'BH' },
  '+974': { name: 'Qatar', code2: 'QA' },
  '+975': { name: 'Bhutan', code2: 'BT' },
  '+976': { name: 'Mongolia', code2: 'MN' },
  '+977': { name: 'Nepal', code2: 'NP' },
  '+992': { name: 'Tajikistan', code2: 'TJ' },
  '+993': { name: 'Turkmenistan', code2: 'TM' },
  '+994': { name: 'Azerbaijan', code2: 'AZ' },
  '+995': { name: 'Georgia', code2: 'GE' },
  '+996': { name: 'Kyrgyzstan', code2: 'KG' },
  '+998': { name: 'Uzbekistan', code2: 'UZ' },
  '+31': { name: 'Netherlands', code2: 'NL' },
  '+60': { name: 'Malaysia', code2: 'MY' },
  '+43': { name: 'Austria', code2: 'AT' },
  '+40': { name: 'Romania', code2: 'RO' },
  '+41': { name: 'Switzerland', code2: 'CH' },
  '+36': { name: 'Hungary', code2: 'HU' },
  '+27': { name: 'South Africa', code2: 'ZA' }
};

function MediaDisplay({ mediaUrl, messageContent }: { mediaUrl: string; messageContent: string }) {
  const getMediaType = (url: string) => {
    if (url.includes('/image/')) return 'image';
    if (url.includes('/video/')) return 'video';
    if (url.includes('/audio/')) return 'audio';
    if (url.includes('/document/')) return 'document';
    return 'unknown';
  };

  const mediaType = getMediaType(mediaUrl);

  switch (mediaType) {
    case 'image':
      return (
        <div className="my-2">
          <img 
            src={mediaUrl} 
            alt="Shared image" 
            className="max-w-xs rounded-lg shadow-sm"
            onError={(e) => {
              console.error('Image failed to load:', mediaUrl);
              e.currentTarget.style.display = 'none';
            }}
          />
          <div className="flex items-center gap-1 mt-1 text-xs text-gray-500">
            <Image className="w-3 h-3" />
            Image
          </div>
        </div>
      );
    
    case 'video':
      return (
        <div className="my-2">
          <video 
            controls 
            className="max-w-xs rounded-lg shadow-sm"
            onError={(e) => {
              console.error('Video failed to load:', mediaUrl);
              e.currentTarget.style.display = 'none';
            }}
          >
            <source src={mediaUrl} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
          <div className="flex items-center gap-1 mt-1 text-xs text-gray-500">
            <Video className="w-3 h-3" />
            Video
          </div>
        </div>
      );
    
    case 'audio':
      return <AudioPlayer url={mediaUrl} />;
    
    case 'document':
      return (
        <div className="my-2">
          <a 
            href={mediaUrl} 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex items-center gap-2 p-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <FileText className="w-4 h-4 text-gray-600" />
            <span className="text-sm text-gray-700">Document</span>
          </a>
        </div>
      );
    
    default:
      return (
        <div className="my-2 p-2 bg-gray-100 rounded-lg">
          <div className="flex items-center gap-1 text-xs text-gray-500">
            <FileText className="w-3 h-3" />
            Media file
          </div>
        </div>
      );
  }
}

function AudioPlayer({ url }: { url: string }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = React.useRef<HTMLAudioElement>(null);

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  return (
    <div className="flex items-center gap-2 my-2">
      <button
        onClick={togglePlay}
        className="p-2 rounded-full bg-green-100 hover:bg-green-200 transition-colors"
      >
        {isPlaying ? (
          <Pause className="w-4 h-4 text-green-700" />
        ) : (
          <Play className="w-4 h-4 text-green-700" />
        )}
      </button>
      <audio
        ref={audioRef}
        src={url}
        onEnded={() => setIsPlaying(false)}
        onPause={() => setIsPlaying(false)}
        onPlay={() => setIsPlaying(true)}
      />
      <div className="flex items-center gap-1 text-sm text-gray-500">
        <Volume2 className="w-3 h-3" />
        {isPlaying ? 'Playing audio...' : 'Audio message'}
      </div>
    </div>
  );
}

// Move NewMessagePanel outside of App component to prevent re-creation
const NewMessagePanel = React.memo(({ 
  phoneNumber, 
  setPhoneNumber, 
  templates, 
  selectedTemplate, 
  handleTemplateChange, 
  templateParams, 
  setTemplateParams, 
  isLoadingTemplates, 
  getTemplateMessage, 
  handleSubmit, 
  isSending, 
  status, 
  expandedThread 
}: {
  phoneNumber: string;
  setPhoneNumber: (value: string) => void;
  templates: Template[];
  selectedTemplate: Template | null;
  handleTemplateChange: (templateName: string) => void;
  templateParams: string[];
  setTemplateParams: (params: string[]) => void;
  isLoadingTemplates: boolean;
  getTemplateMessage: () => string;
  handleSubmit: (e: React.FormEvent) => void;
  isSending: boolean;
  status: string;
  expandedThread: string | null;
}) => (
  <div className="p-6">
    <h2 className="text-xl font-semibold mb-6">New Message</h2>
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700 mb-1">
          Phone Number
        </label>
        <input
          type="text"
          id="phoneNumber"
          value={phoneNumber}
          onChange={(e) => setPhoneNumber(e.target.value)}
          placeholder="+1234567890"
          className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
          required
          disabled={isSending || !!expandedThread}
        />
        <p className="mt-1 text-sm text-gray-500">Include country code (e.g., +1 for US)</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Template
        </label>
        <select
          value={selectedTemplate?.name || ''}
          onChange={(e) => handleTemplateChange(e.target.value)}
          className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
          disabled={isLoadingTemplates}
        >
          {isLoadingTemplates ? (
            <option>Loading templates...</option>
          ) : (
            templates.map((template) => (
              <option key={template.name} value={template.name}>
                {template.name}
              </option>
            ))
          )}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Learner Name
        </label>
        <input
          type="text"
          value={templateParams[0]}
          onChange={(e) => {
            const newParams = [...templateParams];
            newParams[0] = e.target.value;
            setTemplateParams(newParams);
          }}
          className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
          placeholder="Enter learner's name"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {selectedTemplate?.name === 'es_welcome_started_no_lessons_booked' ? 'Consultant Name' : 'Company/LC Name'}
        </label>
        <input
          type="text"
          value={templateParams[1]}
          onChange={(e) => {
            const newParams = [...templateParams];
            newParams[1] = e.target.value;
            setTemplateParams(newParams);
          }}
          className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
          placeholder={selectedTemplate?.name === 'es_welcome_started_no_lessons_booked' ? "Enter consultant's name" : "Enter company name/LC Name"}
          required
        />
      </div>

      {selectedTemplate?.name === 'es_welcome_started_no_lessons_booked' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Company/LC Name
          </label>
          <input
            type="text"
            value={templateParams[2]}
            onChange={(e) => {
              const newParams = [...templateParams];
              newParams[2] = e.target.value;
              setTemplateParams(newParams);
            }}
            className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            placeholder="Enter company name / LC Name"
            required
          />
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Preview
        </label>
        <div className="p-4 bg-gray-50 rounded-lg whitespace-pre-wrap">
          {getTemplateMessage()}
        </div>
      </div>

      <div className="flex items-center justify-between pt-2">
        <button
          type="submit"
          disabled={isSending || !templateParams[0]}
          className={`flex items-center px-6 py-2 rounded-lg text-white font-medium transition-colors ${
            isSending || !templateParams[0]
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-green-600 hover:bg-green-700'
          }`}
        >
          <Send className="w-4 h-4 mr-2" />
          {isSending ? 'Sending...' : 'Send Message'}
        </button>

        {status && (
          <p className={`text-sm ${status.includes('success') ? 'text-green-600' : 'text-red-600'}`}>
            {status}
          </p>
        )}
      </div>
    </form>
  </div>
));

// Move CountriesPanel outside of App component
const CountriesPanel = React.memo(({ 
  countryCodeSummaries, 
  selectedCountryFilter, 
  handleCountryFilter, 
  clearCountryFilter 
}: {
  countryCodeSummaries: CountryCodeSummary[];
  selectedCountryFilter: string | null;
  handleCountryFilter: (countryCode: string) => void;
  clearCountryFilter: () => void;
}) => (
  <div className="p-2 h-full flex flex-col">
    <div className="flex items-center justify-between mb-2 flex-shrink-0">
      <h3 className="text-xs font-medium text-gray-700">Countries</h3>
      {selectedCountryFilter && (
        <button
          onClick={clearCountryFilter}
          className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"
        >
          <X className="w-3 h-3" />
        </button>
      )}
    </div>
    
    {/* Ultra compact vertical scrolling grid for countries */}
    <div className="flex-1 overflow-y-auto">
      <div className="grid grid-cols-3 gap-1">
        {countryCodeSummaries.map(({ countryCode, unreadCount }) => {
          const countryInfo = COUNTRY_CODES[countryCode];
          return (
            <button
              key={countryCode}
              onClick={() => handleCountryFilter(countryCode)}
              className={`relative flex flex-col items-center justify-center px-1 py-1.5 rounded text-xs font-medium transition-colors min-h-[32px] ${
                selectedCountryFilter === countryCode
                  ? 'bg-green-100 text-green-800 border border-green-300'
                  : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center gap-0.5">
                <span className="font-bold text-xs leading-none">
                  {countryCode}
                </span>
                <span className="font-bold text-gray-600 text-xs leading-none">
                  {countryInfo?.code2 || countryCode.replace('+', '')}
                </span>
              </div>
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 inline-flex items-center justify-center w-4 h-4 text-xs font-bold text-white bg-red-500 rounded-full">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  </div>
));

// Move ConversationsListPanel outside of App component
const ConversationsListPanel = React.memo(({ 
  selectedCountryFilter, 
  isLoadingMessages, 
  fetchMessages, 
  filteredThreads, 
  messages, 
  expandedThread, 
  handleThreadClick, 
  formatPhoneNumber, 
  formatDate 
}: {
  selectedCountryFilter: string | null;
  isLoadingMessages: boolean;
  fetchMessages: () => void;
  filteredThreads: any[];
  messages: Message[];
  expandedThread: string | null;
  handleThreadClick: (phoneNumber: string) => void;
  formatPhoneNumber: (number: string) => string;
  formatDate: (timestamp: string) => string;
}) => (
  <>
    <div className="flex items-center justify-between p-4 border-b flex-shrink-0">
      <h2 className="text-xl font-semibold">
        Conversations
        {selectedCountryFilter && (
          <span className="text-sm font-normal text-gray-500 ml-2">
            ({COUNTRY_CODES[selectedCountryFilter]?.code2 || selectedCountryFilter})
          </span>
        )}
      </h2>
      <button
        onClick={fetchMessages}
        disabled={isLoadingMessages}
        className="flex items-center px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900"
      >
        <RefreshCw className={`w-4 h-4 mr-1.5 ${isLoadingMessages ? 'animate-spin' : ''}`} />
        Refresh
      </button>
    </div>
    
    <div className="overflow-y-auto flex-1">
      {isLoadingMessages ? (
        <div className="text-center py-8 text-gray-500">Loading conversations...</div>
      ) : filteredThreads.length > 0 ? (
        filteredThreads.map(({ phoneNumber, latestMessage, hasReceivedMessage, contactName }) => {
          const isSelected = expandedThread === phoneNumber;
          const hasUnread = messages.some(msg => 
            !msg.is_read && 
            (msg.from_number === phoneNumber || msg.to_number === phoneNumber)
          );
          
          return (
            <button
              key={phoneNumber}
              onClick={() => handleThreadClick(phoneNumber)}
              className={`w-full px-4 py-3 flex items-start hover:bg-gray-50 transition-colors ${
                isSelected ? 'bg-green-50' : ''
              }`}
            >
              <div className="flex-1 text-left">
                <div className="flex items-center justify-between">
                  <h3 className={`${hasUnread ? 'font-bold' : 'font-medium'} text-gray-900`}>
                    {contactName || formatPhoneNumber(phoneNumber)}
                  </h3>
                  {hasUnread && (
                    <span className="inline-flex items-center justify-center w-2 h-2 bg-red-500 rounded-full ml-2"></span>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {formatPhoneNumber(phoneNumber)}
                </p>
                <p className={`text-sm ${hasUnread ? 'font-semibold text-gray-900' : 'text-gray-500'} mt-1 line-clamp-2`}>
                  {latestMessage.content}
                </p>
                <time className="text-xs text-gray-400 mt-1">
                  {formatDate(latestMessage.timestamp)}
                </time>
              </div>
            </button>
          );
        })
      ) : selectedCountryFilter ? (
        <div className="text-center py-8 text-gray-500">
          No conversations from {COUNTRY_CODES[selectedCountryFilter]?.name || selectedCountryFilter}
        </div>
      ) : (
        <div className="text-center py-8 text-gray-500">No conversations yet</div>
      )}
    </div>
  </>
));

// Move MessageViewPanel outside of App component
const MessageViewPanel = React.memo(({ 
  expandedThread, 
  formatPhoneNumber, 
  threadedMessages, 
  formatTime, 
  message, 
  setMessage, 
  isMessageTooLong, 
  messageLength, 
  MAX_MESSAGE_LENGTH, 
  handleSubmit, 
  isSending 
}: {
  expandedThread: string | null;
  formatPhoneNumber: (number: string) => string;
  threadedMessages: { [key: string]: Message[] };
  formatTime: (timestamp: string) => string;
  message: string;
  setMessage: (value: string) => void;
  isMessageTooLong: boolean;
  messageLength: number;
  MAX_MESSAGE_LENGTH: number;
  handleSubmit: (e: React.FormEvent) => void;
  isSending: boolean;
}) => (
  <>
    {expandedThread ? (
      <>
        <div className="p-4 border-b flex-shrink-0">
          <h3 className="text-lg font-semibold">{formatPhoneNumber(expandedThread)}</h3>
        </div>
        
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {threadedMessages[expandedThread]?.map((msg, index) => {
            const showDate = index === 0 || 
              new Date(msg.timestamp).toDateString() !== 
              new Date(threadedMessages[expandedThread][index - 1].timestamp).toDateString();

            return (
              <div key={msg.id}>
                {showDate && (
                  <div className="text-center my-4">
                    <span className="text-xs text-gray-500 bg-white px-2 py-1 rounded-full">
                      {new Date(msg.timestamp).toLocaleDateString()}
                    </span>
                  </div>
                )}
                <div className={`flex ${msg.type === 'sent' ? 'justify-end' : 'justify-start'}`}>
                  <div
                    className={`max-w-[75%] rounded-lg px-4 py-2 ${
                      msg.type === 'sent'
                        ? 'bg-green-100 text-green-900'
                        : 'bg-white text-gray-900 border border-gray-100'
                    }`}
                  >
                    <p className="text-sm whitespace-pre-wrap break-words">
                      {msg.content}
                    </p>
                    {msg.media_url && (
                      <MediaDisplay mediaUrl={msg.media_url} messageContent={msg.content} />
                    )}
                    <div className="flex justify-end items-center gap-2 mt-1">
                      <time className="text-xs text-gray-500">
                        {formatTime(msg.timestamp)}
                      </time>
                      {msg.type === 'sent' && msg.status && (
                        <span className="text-xs text-gray-500">
                          {msg.status}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="p-4 border-t bg-white flex-shrink-0">
          <div className="relative">
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
              className={`w-full px-4 py-2 border rounded-lg resize-none ${
                isMessageTooLong ? 'border-red-300' : 'border-gray-200'
              } focus:ring-2 focus:ring-green-500 focus:border-transparent`}
              placeholder="Type your reply..."
            />
            <div className="absolute bottom-2 right-2 flex items-center gap-2">
              {isMessageTooLong && (
                <AlertCircle className="w-4 h-4 text-red-500" />
              )}
              <span className={`text-xs ${
                isMessageTooLong ? 'text-red-500' : 'text-gray-500'
              }`}>
                {messageLength}/{MAX_MESSAGE_LENGTH}
              </span>
            </div>
          </div>
          <div className="flex justify-end mt-2">
            <button
              onClick={handleSubmit}
              disabled={isSending || isMessageTooLong || !message.trim()}
              className={`flex items-center px-4 py-2 rounded-lg text-white font-medium transition-colors ${
                isSending || isMessageTooLong || !message.trim()
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-green-600 hover:bg-green-700'
              }`}
            >
              <Send className="w-4 h-4 mr-2" />
              {isSending ? 'Sending...' : 'Send'}
            </button>
          </div>
        </div>
      </>
    ) : (
      <div className="flex-1 flex items-center justify-center text-gray-500">
        Select a conversation to view messages
      </div>
    )}
  </>
));

function App() {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [serverError, setServerError] = useState('');
  const [expandedThread, setExpandedThread] = useState<string | null>(null);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [templateParams, setTemplateParams] = useState<string[]>(['', '', '']);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [selectedCountryFilter, setSelectedCountryFilter] = useState<string | null>(null);

  const MAX_MESSAGE_LENGTH = 4096;
  const messageLength = message.length;
  const isMessageTooLong = messageLength > MAX_MESSAGE_LENGTH;

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      const response = await fetch('/auth/user', {
        credentials: 'include',
        headers: {
          'Accept': 'application/json'
        }
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          setUser(null);
          return;
        }
        throw new Error('Network response was not ok');
      }
      
      const data = await response.json();
      setUser(data.user);
    } catch (error) {
      console.error('Auth check failed:', error);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = () => {
    window.location.href = '/auth/google';
  };

  const handleLogout = async () => {
    try {
      await fetch('/auth/logout', {
        method: 'POST',
        credentials: 'include'
      });
      setUser(null);
      window.location.href = '/';
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const fetchTemplates = async () => {
    setIsLoadingTemplates(true);
    try {
      const response = await fetch('/api/templates');
      if (!response.ok) {
        throw new Error('Failed to fetch templates');
      }
      const data = await response.json();
      if (data.success) {
        setTemplates(data.templates);
        if (data.templates.length > 0) {
          setSelectedTemplate(data.templates[0]);
        }
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
      setServerError('Failed to load message templates');
    } finally {
      setIsLoadingTemplates(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchMessages = async () => {
    setIsLoadingMessages(true);
    setServerError('');
    try {
      const response = await fetch('/api/messages');
      if (!response.ok) {
        throw new Error(`Server returned ${response.status}: ${response.statusText}`);
      }
      const data = await response.json();
      if (data.success) {
        setMessages(data.messages);
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
      setServerError('Unable to connect to server. Please try again later.');
    } finally {
      setIsLoadingMessages(false);
    }
  };

  useEffect(() => {
    fetchMessages();
    const interval = setInterval(fetchMessages, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleTemplateChange = (templateName: string) => {
    const template = templates.find(t => t.name === templateName);
    if (template) {
      setSelectedTemplate(template);
      const initialParams = template.name === 'es_welcome_started_no_lessons_booked' 
        ? ['', '', ''] 
        : ['', '']; 
      setTemplateParams(initialParams);
    }
  };

  const getTemplateMessage = () => {
    if (!selectedTemplate) return '';
    const bodyComponent = selectedTemplate.components.find(comp => comp.type === 'body');
    if (!bodyComponent || !bodyComponent.text) return '';
    
    let message = bodyComponent.text;
    templateParams.forEach((param, index) => {
      message = message.replace(`{{${index + 1}}}`, param);
    });
    return message;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isMessageTooLong) return;
    
    setIsSending(true);
    setStatus('Sending...');
    setServerError('');

    const messagePayload = expandedThread
      ? { text: message.trim() }
      : {
          template: {
            name: selectedTemplate!.name,
            language: selectedTemplate!.language,
            components: [
              {
                type: "body",
                parameters: templateParams
                  .filter(param => param.trim() !== '')
                  .map(param => ({
                    type: "text",
                    text: param.trim()
                  }))
              }
            ]
          }
        };

    try {
      const response = await fetch('/api/send-message', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          recipientPhoneNumber: expandedThread || phoneNumber.trim(),
          message: messagePayload,
          useTemplate: !expandedThread
        }),
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || `Server returned ${response.status}: ${response.statusText}`);
      }

      if (data.success) {
        setStatus('Message sent successfully!');
        setMessage('');
        setTemplateParams(['', '', '']);
        fetchMessages();
      } else {
        setStatus(`Failed to send message: ${data.error}`);
      }
    } catch (error) {
      console.error('Error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      setStatus(`Failed to send message: ${errorMessage}`);
      setServerError('Failed to send message. Please try again.');
    } finally {
      setIsSending(false);
    }
  };

  const handleThreadClick = async (phoneNumber: string) => {
    setExpandedThread(expandedThread === phoneNumber ? null : phoneNumber);
    
    if (expandedThread !== phoneNumber) {
      try {
        await fetch(`/api/conversations/${phoneNumber}/read`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' }
        });
        setMessages(prevMessages => 
          prevMessages.map(msg => 
            (msg.from_number === phoneNumber || msg.to_number === phoneNumber)
              ? { ...msg, is_read: 1 }
              : msg
          )
        );
      } catch (error) {
        console.error('Error marking conversation as read:', error);
      }
    }
  };

  // Extract country code from phone number
  const getCountryCode = (phoneNumber: string): string => {
    if (!phoneNumber.startsWith('+')) return 'Unknown';
    
    // Sort country codes by length (longest first) to match the most specific code
    const sortedCodes = Object.keys(COUNTRY_CODES).sort((a, b) => b.length - a.length);
    
    for (const code of sortedCodes) {
      if (phoneNumber.startsWith(code)) {
        return code;
      }
    }
    
    return 'Unknown';
  };

  const threadedMessages = messages.reduce<{ [key: string]: Message[] }>((threads, msg) => {
    const threadKey = msg.type === 'sent' ? msg.to_number : msg.from_number;
    if (!threads[threadKey]) {
      threads[threadKey] = [];
    }
    threads[threadKey].push(msg);
    return threads;
  }, {});

  const sortedThreads = Object.entries(threadedMessages).map(([phoneNumber, messages]) => {
    const sortedMessages = messages.sort((a, b) => 
      new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    );
    
    const latestMessage = sortedMessages[sortedMessages.length - 1];
    const hasReceivedMessage = sortedMessages.some(msg => msg.type === 'received');
    
    // Get the contact name from the most recent received message
    const contactName = sortedMessages
      .filter(msg => msg.type === 'received' && msg.sender_name && msg.sender_name !== 'Unknown' && msg.sender_name !== 'System')
      .pop()?.sender_name || null;
    
    return {
      phoneNumber,
      messages: sortedMessages,
      latestMessage,
      hasReceivedMessage,
      contactName
    };
  }).sort((a, b) => 
    new Date(b.latestMessage.timestamp).getTime() - new Date(a.latestMessage.timestamp).getTime()
  );

  // Calculate country code summaries
  const countryCodeSummaries: CountryCodeSummary[] = React.useMemo(() => {
    const summaryMap: { [key: string]: CountryCodeSummary } = {};

    sortedThreads.forEach(({ phoneNumber, messages }) => {
      const countryCode = getCountryCode(phoneNumber);
      
      if (!summaryMap[countryCode]) {
        summaryMap[countryCode] = {
          countryCode,
          unreadCount: 0,
          totalConversations: 0
        };
      }

      summaryMap[countryCode].totalConversations++;
      
      // Count unread messages in this conversation
      const unreadInConversation = messages.filter(msg => !msg.is_read && msg.type === 'received').length;
      summaryMap[countryCode].unreadCount += unreadInConversation;
    });

    // Convert to array and sort by unread count (highest first), then by country code
    return Object.values(summaryMap)
      .filter(summary => summary.totalConversations > 0)
      .sort((a, b) => {
        if (a.unreadCount !== b.unreadCount) {
          return b.unreadCount - a.unreadCount;
        }
        return a.countryCode.localeCompare(b.countryCode);
      });
  }, [sortedThreads]);

  // Filter threads by selected country
  const filteredThreads = selectedCountryFilter 
    ? sortedThreads.filter(({ phoneNumber }) => getCountryCode(phoneNumber) === selectedCountryFilter)
    : sortedThreads;

  const handleCountryFilter = (countryCode: string) => {
    setSelectedCountryFilter(selectedCountryFilter === countryCode ? null : countryCode);
    setExpandedThread(null); // Close any open conversation when filtering
  };

  const clearCountryFilter = () => {
    setSelectedCountryFilter(null);
  };

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (timestamp: string) => {
    return new Date(timestamp).toLocaleString([], {
      month: 'numeric',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatPhoneNumber = (number: string) => {
    return number.startsWith('+') ? number : `+${number}`;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-sm text-center">
          <MessageSquare className="w-12 h-12 text-green-600 mx-auto mb-4" />
          <h1 className="text-2xl font-semibold text-gray-800 mb-6">WhatsApp Integration</h1>
          <button
            onClick={handleLogin}
            className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 transition-colors"
          >
            Sign in with Google
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      <div className="h-16 bg-white border-b flex items-center justify-between px-6 flex-shrink-0">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-8 h-8 text-green-600" />
          <h1 className="text-xl font-semibold text-gray-800">WhatsApp Integration</h1>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-gray-600">{user.email}</span>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900"
          >
            <LogOut className="w-4 h-4" />
            Sign Out
          </button>
        </div>
      </div>

      {serverError && (
        <div className="mx-6 mt-4 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-2 text-red-700 flex-shrink-0">
          <AlertCircle className="w-5 h-5" />
          <p>{serverError}</p>
        </div>
      )}

      <ResizableLayout>
        <NewMessagePanel
          phoneNumber={phoneNumber}
          setPhoneNumber={setPhoneNumber}
          templates={templates}
          selectedTemplate={selectedTemplate}
          handleTemplateChange={handleTemplateChange}
          templateParams={templateParams}
          setTemplateParams={setTemplateParams}
          isLoadingTemplates={isLoadingTemplates}
          getTemplateMessage={getTemplateMessage}
          handleSubmit={handleSubmit}
          isSending={isSending}
          status={status}
          expandedThread={expandedThread}
        />
        <CountriesPanel
          countryCodeSummaries={countryCodeSummaries}
          selectedCountryFilter={selectedCountryFilter}
          handleCountryFilter={handleCountryFilter}
          clearCountryFilter={clearCountryFilter}
        />
        <ConversationsListPanel
          selectedCountryFilter={selectedCountryFilter}
          isLoadingMessages={isLoadingMessages}
          fetchMessages={fetchMessages}
          filteredThreads={filteredThreads}
          messages={messages}
          expandedThread={expandedThread}
          handleThreadClick={handleThreadClick}
          formatPhoneNumber={formatPhoneNumber}
          formatDate={formatDate}
        />
        <MessageViewPanel
          expandedThread={expandedThread}
          formatPhoneNumber={formatPhoneNumber}
          threadedMessages={threadedMessages}
          formatTime={formatTime}
          message={message}
          setMessage={setMessage}
          isMessageTooLong={isMessageTooLong}
          messageLength={messageLength}
          MAX_MESSAGE_LENGTH={MAX_MESSAGE_LENGTH}
          handleSubmit={handleSubmit}
          isSending={isSending}
        />
      </ResizableLayout>
    </div>
  );
}

export default App;
